import { KNOWN_CATEGORIES } from './categories.ts';
import type {
  GroundTruthPageEntry,
  GroundTruthSource,
  GroundTruthV1,
  Submission,
  SubmissionPage,
} from './types.ts';

const FLAGGED_STATUSES: ReadonlySet<string> = new Set(['low_confidence', 'unclassified', 'needs_ocr']);

function isFlagged(page: SubmissionPage): boolean {
  return page.status !== null && FLAGGED_STATUSES.has(page.status);
}

// Returns the page_index of every flagged page that has not been reviewed.
// Promotion is blocked while this list is non-empty.
export function unreviewedFlaggedPages(pages: readonly SubmissionPage[]): readonly number[] {
  return pages
    .filter((p) => isFlagged(p) && p.review_decision === null)
    .map((p) => p.page_index);
}

function categoryTypeOf(value: string | null): GroundTruthPageEntry['category_type'] {
  if (value === null) return 'unclassified';
  const known = KNOWN_CATEGORIES.find((c) => c.value === value);
  if (known) return known.group;
  // Free-text "Other" categories the reviewer typed but we haven't catalogued —
  // treat as discipline to avoid losing the signal; the confusion matrix will
  // group them under __other__ at grade time.
  return 'discipline';
}

// Build the V1 ground-truth snapshot for a single submission. Pure transform —
// caller decides when to persist. Excluded from grading: pages with
// review_decision='skip' are still represented (so we can audit later) but the
// grader skips them based on `source` + downstream rules.
export function buildPageEntry(page: SubmissionPage): GroundTruthPageEntry {
  let category: string | null;
  let source: GroundTruthSource;

  if (page.review_decision === 'reject') {
    category = page.review_human_category;
    source = 'human';
  } else if (page.review_decision === 'approve') {
    category = page.category;
    source = 'predicted_approved';
  } else if (page.review_decision === 'skip') {
    // Skip preserves the predicted value for the audit trail; grader excludes
    // it via the `source === 'skipped'` check.
    category = page.category;
    source = 'skipped';
  } else {
    // null decision — only valid for unflagged pages once promotion is allowed.
    category = page.category;
    source = 'predicted_unreviewed';
  }

  return {
    page_index: page.page_index,
    page_label: page.page_label,
    category,
    category_type: categoryTypeOf(category),
    source,
  };
}

export interface BuildGroundTruthInput {
  pages: readonly SubmissionPage[];
  promotedFromStatus: Submission['status'];
  now?: string;
}

export function buildGroundTruth(input: BuildGroundTruthInput): GroundTruthV1 {
  const { pages, promotedFromStatus, now } = input;
  const sorted = [...pages].sort((a, b) => a.page_index - b.page_index);
  return {
    version: 1,
    promoted_at: now ?? new Date().toISOString(),
    promoted_from_status: promotedFromStatus,
    pages: sorted.map(buildPageEntry),
  };
}
